import { ICredentials } from '../models/ICredentials';
import { authService } from '../services/AuthService';
import { EventBus } from '../EventBus';
import Passenger from '@/components/Passenger/models/Passenger';
import { PassengerGroup } from '@/models/PassengerGroup';
import PassengerGroupPassenger from '@/models/PassengerGroupPassenger';
import User, { PassengerGroupPermissionType } from '@/models/User';
import { PermissionEnum, RolePermission } from '@/models/DestinationRole';
import PermissionService from '@/services/PermissionService';
import { Route } from 'vue-router';
import Routes from '@/Routes';
import { IPassengerGroupWithPassengers } from '@/typings/timit';
import { Organization } from '@/models/Organization';
import { hasPassengerPermission } from '@/services/RideitAuthService';

interface IAuthState {
    token: string;
    status: string;
}

const state = { token: localStorage.getItem('auth-token') || '', status: '' };

const getters = {
  isAuthenticated: (authState: any) => !!authState.token,
  authStatus: (authState: any) => authState.status,
  authToken: (authState: any) => authState.token,
  hasPassengerPermission: (authState: IAuthState, authGetters: any, rootState: any, rootGetters: any) => 
    (permissions: RolePermission[], routerRoute: Route | null = null) =>  {
      const user = (rootState.user.me as User);
      const route = routerRoute ? routerRoute : rootState.route;

      const permissionLevel = PermissionService.getPassengerPermissionLevel(permissions);

      if (route.params && route.params.passengerId) {
        const passengerId = parseInt(route.params.passengerId, 10);
        return handlePassengerPermissions(rootState, rootGetters, passengerId, permissionLevel);
      }

      if (route.params && route.params.passengerGroupId) {
        const passengerGroupId = parseInt(route.params.passengerGroupId, 10);
        return handlePassengerGroupPermissions(user, passengerGroupId, permissionLevel);
      }
      
      if (route.params && route.params.destinationId) {
        const destinationId = parseInt(route.params.destinationId, 10);
        return handlePassengerDestinationPermissions(user, destinationId, permissionLevel);
      }
      
      return false;
  },
  hasDestinationPermission: (authState: IAuthState, authGetters: any, rootState: any) => 
    (destinationId: string, permissions: number[]) =>  {
      const numericDestinationId = parseInt(destinationId, 10);
      if (isNaN(numericDestinationId)) {
        return false;
      }

      const user = (rootState.user.me as User);
      const destinationRole = user.destinationRoles.find(dr => dr.destinationId === numericDestinationId);
      if (!destinationRole) {
        return false;
      }

      return permissions.every(p => destinationRole.permissions.includes(p));
  },
  hasTransportationPermission: (authState: IAuthState, authGetters: any, rootState: any,  rootGetters: any) => 
    (permissions: number[], routerRoute: Route | null = null) => {
      const permissionLevel = PermissionService.getTransportationPermissionLevel(permissions);
      const user: User = rootState.user.me;
      const route = routerRoute ? routerRoute : rootState.route;

      if (route.params && route.params.passengerId) {
        const passengerId = parseInt(route.params.passengerId, 10);
        const passenger: Passenger = rootGetters['passenger/findPassengerById'](passengerId);
        if (!passenger) {
          return false;
        }
        if (passenger.passengerDestinations.length === 0) {
          const selectedPassengerGroup: IPassengerGroupWithPassengers = rootState.organization.selectedPassengerGroup;
          if (
            selectedPassengerGroup 
            && selectedPassengerGroup.passengerGroupPassengers 
            && selectedPassengerGroup.passengerGroupPassengers.some(pgp => pgp.id === passengerId)
            ) {
              const userPermission = user.passengerGroupRoles
              .find(pgr => pgr.passengerGroupId === selectedPassengerGroup.id);
              return (userPermission.transportationPermission || 0) >= permissionLevel;
            }
          return false;
        }
       
        return user.destinationRoles
          .some(dr => passenger.passengerDestinations
            .find(pd => pd.destinationId === dr.destinationId)
            && dr.transportationPermission >= permissionLevel
          );
      }

      if (route.params && route.params.destinationId) {
        const destinationId = parseInt(route.params.destinationId, 10);
        return user.destinationRoles
          .some(dr => dr.destinationId === destinationId && dr.transportationPermission >= permissionLevel);
      }
      return false;
  },
  hasSchedulePermission: (authState: IAuthState, authGetters: any, rootState: any,  rootGetters: any) => 
    (permissionLevel: PermissionEnum, passengerGroupIds: number[], routerRoute: Route | null = null) => {
      const user: User = rootState.user.me;
      const route = routerRoute ? routerRoute : rootState.route;
      let hasPermissionToPassengerGroup = false;
      let hasPermissionToDestination = false;
      if (route.params && route.params.passengerId) {
        const passengerId = parseInt(route.params.passengerId, 10);
        const passenger: Passenger = rootGetters['passenger/findPassengerById'](passengerId);
        if (!passenger) {
          return false;
        }

        if (passengerGroupIds.length > 0) {
          hasPermissionToPassengerGroup = user.passengerGroupRoles
          .filter(r => passengerGroupIds.includes(r.passengerGroupId))
          .some(r => (r.schedulePermission || 0) >= permissionLevel);
        }

        hasPermissionToDestination = user.destinationRoles
        .some(dr => passenger.passengerDestinations
          .find(pd => pd.destinationId === dr.destinationId)
          && dr.schedulePermission >= permissionLevel
        );

        return hasPermissionToDestination || hasPermissionToPassengerGroup; 
      }
  },
  hasRideitPermission: (authState: IAuthState, authGetters: any, rootState: any, rootGetters: any) => 
    (passengerId: number, permissions: RolePermission[]) => {
    const permissionLevel = PermissionService.getRideitPermissionLevel(permissions);
    return hasPassengerPermission(passengerId, permissionLevel, { state: rootState, getters: rootGetters });
  },
  isOrganizationAdmin: (authState: IAuthState, authGetters: any, rootState: any) => (organizationId: string) => {
    const user = rootState.user.me;
    const organization = (rootState.organization.organizations as Organization[]).find(o => o.id === organizationId);
    if (!organization) {
      return false;
    }

    return organization.administratorIds.includes(user.id);
  },
  organizationCanSeeSsn: (authState: IAuthState, authGetters: any, rootState: any) => (organizationId: string) => {
    const organization = (rootState.organization.organizations as Organization[]).find(o => o.id === organizationId);
    if(organization && organization.showSsn) {
      return true
    }
    return false;
  }
};

const actions = {
    async authRequest({commit, dispatch}: {commit: any, dispatch: any} , credentials: ICredentials) {
        commit('authRequest');
        try {
            const result: string = await authService.loginAsync(credentials);
            localStorage.setItem('auth-token', result); // stash the auth token in localStorage
            commit('authSuccess', result);
            EventBus.$emit('logged-in', null);
            // dispatch('user/userRequest', null, { root: true });
            return result;
        } catch (ex) {
            commit('authError', ex);
            localStorage.removeItem('auth-token');
            throw ex;
        }
    },
    async azureAdSignInOrSignUp({commit, dispatch}: {commit: any, dispatch: any}) {
      try {
          const result: string = await authService.azureAdSignInOrSignUp();
          localStorage.setItem('auth-token', result); // stash the auth token in localStorage
          commit('authSuccess', result);
          EventBus.$emit('logged-in', null);
          return result;
      } catch (ex) {
        commit('authError', ex);
        localStorage.removeItem('auth-token');
        throw ex;
      }
    },
    authLogout: ({commit, dispatch}: {commit: any, dispatch: any}) => {
        return new Promise<void>((resolve, reject) => {
          commit('authLogout');
          localStorage.removeItem('auth-token');
          resolve();
        });
    },
};

const mutations = {
    authRequest: (authState: any) => {
        authState.status = 'attempting authentication request';
    },
    authSuccess: (authState: any, authToken: string) => {
        authState.status = 'authentication succeeded';
        authState.token = authToken;
    },
    authError: (authState: any) => {
        authState.status = 'error';
    },
    authLogout: (authState: any) => {
        authState.token = '';
    },
};

const handlePassengerPermissions = (rootState: any, rootGetters: any, passengerId: number, permissionLevel: PermissionEnum) => {
  const user = (rootState.user.me as User);
  const passenger: Passenger = rootGetters['passenger/findPassengerById'](passengerId);
  if (!passenger) {
    return false;
  }
  const destinationIds = passenger.passengerDestinations.map(pd => pd.destinationId);
  const hasDestinationPermission = destinationIds
    .some(dId => user.destinationRoles
      .find(dr => dr.destinationId === dId && dr.passengerPermission >= permissionLevel));
  if (hasDestinationPermission) {
    return true;
  }

  const passengerGroupsIds = (rootState.organization.passengerGroups as PassengerGroup[])
    .map(pg => pg.passengerGroupPassengers)
    .reduce((pgp: PassengerGroupPassenger[], c) => pgp = pgp.concat(c), [])
    .filter(pgp => pgp)
    .filter(pgp => pgp.passengerId && pgp.passengerId === passenger.id)
    .map(pgp => pgp.passengerGroupId);

  const hasPassengerGroupPermission = passengerGroupsIds
    .some(pgId => user.passengerGroupRoles
      .find(pgr => pgr.passengerGroupId === pgId && pgr.passengerPermission >= permissionLevel));

  if (hasPassengerGroupPermission) {
    return true;
  }

  const selectedPassengerGroup = (rootState.organization.selectedPassengerGroup as IPassengerGroupWithPassengers);
  if (
    selectedPassengerGroup 
    && selectedPassengerGroup.passengerGroupPassengers 
    && selectedPassengerGroup.passengerGroupPassengers.some(pgp => pgp.id === passenger.id)
  ) {
    return user.passengerGroupRoles
      .some(pgr => pgr.passengerGroupId === selectedPassengerGroup.id && pgr.passengerPermission >= permissionLevel);
  }

  return false;
}

const handlePassengerGroupPermissions = (user: User, passengerGroupId: number, permissionLevel: PermissionEnum) => {
  const passengerGroup = user.passengerGroupRoles.find(pg => pg.passengerGroupId === passengerGroupId);
  if (!passengerGroup) {
    return false;
  }
  return passengerGroup.passengerPermission >= permissionLevel;
}

const handlePassengerDestinationPermissions = (user: User, destinationId: number, permissionLevel: PermissionEnum) => {
  const destination = user.destinationRoles.find(dr => dr.destinationId === destinationId);
  if (!destination) {
    return false;
  }
  return destination.passengerPermission >= permissionLevel;
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
